import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { useFmtPrice } from '../org/OrgContext.jsx'

// Admin: payments overview + the app-side cancellation hook. Cancelling a
// PAID payment triggers a Stripe refund (the webhook then converges state);
// cancelling a PENDING one just voids it and releases the seat.
//
// OVERDUE (2026-08-01) is not a status — the server computes it per row from
// `organization.invoice_due_days` and sends `is_overdue`, `due_date`,
// `days_overdue` and the reminder counters along with the list. Late bills
// sort to the TOP whatever the filter, and "Overdue" is a filter of its own.
// Nothing on this page notifies anybody: the reminder cron does the chasing
// (docs/CRON.md), this is pure visibility.
//
// An overdue invoice is deliberately NOT cancellable-by-default reasoning:
// cancelling a monthly bill orphans its presences for ever
// (`attendances.payment_item_id` is never reset), so it stays listed until it
// is paid — the Cancel button is still there for the rare real mistake.

export function AdminPaymentsPage() {
  const { t } = useTranslation()
  const { user, loading: authLoading } = useAuth()
  const fmtPrice = useFmtPrice()
  const [rows, setRows] = useState([])
  const [dueDays, setDueDays] = useState(null)
  const [status, setStatus] = useState('')
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState(null)
  const [error, setError] = useState(null)

  function reload(st = status) {
    setLoading(true); setError(null)
    api.admin.payments.list(st)
      .then((d) => {
        setRows(d.payments || [])
        if (d.due_days != null) setDueDays(Number(d.due_days))
      })
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }

  const overdueCount = rows.filter((p) => Number(p.is_overdue) === 1).length

  useEffect(() => {
    if (authLoading || !user || user.role !== 'admin') return
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, status])

  async function cancel(p) {
    const key = p.status === 'paid' ? 'confirm_refund' : 'confirm_cancel'
    if (!confirm(t(`admin_payments.${key}`, { name: p.user_name, amount: fmtPrice(p.amount) }))) return
    setBusyId(p.id); setError(null)
    try {
      await api.admin.payments.cancel(p.id)
      reload()
    } catch (e) {
      setError(e.code || 'server')
    } finally { setBusyId(null) }
  }

  if (authLoading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!user || user.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }

  return (
    <main className="container">
      <p><Link to="/admin">← {t('admin.title')}</Link></p>
      <h1>{t('admin_payments.title')}</h1>
      <p className="muted">{t('admin_payments.intro')}</p>

      <label style={{ maxWidth: '20rem' }}>
        <span>{t('admin_payments.filter_status')}</span>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t('admin_payments.all_statuses')}</option>
          {/* Not a real status — the server computes it, see listAdmin. */}
          <option value="overdue">{t('admin_payments.status_overdue')}</option>
          {['pending', 'paid', 'failed', 'refunded', 'cancelled'].map((s) => (
            <option key={s} value={s}>{t(`admin_payments.status_${s}`)}</option>
          ))}
        </select>
      </label>

      {!loading && overdueCount > 0 && status !== 'overdue' && (
        <p className="error">
          {t('admin_payments.overdue_summary', { count: overdueCount })}{' '}
          <button type="button" className="link-button" onClick={() => setStatus('overdue')}>
            {t('admin_payments.show_overdue')}
          </button>
        </p>
      )}
      {dueDays != null && (
        <p className="muted small">{t('admin_payments.overdue_hint', { count: dueDays })}</p>
      )}

      {error && <p className="error">{t(`admin_payments.errors.${error}`, { defaultValue: error })}</p>}
      {loading && <p>{t('common.loading')}</p>}
      {!loading && rows.length === 0 && <p className="muted">{t('admin_payments.empty_state')}</p>}

      {!loading && rows.length > 0 && (
        <table className="results-table">
          <thead>
            <tr>
              <th>{t('admin_payments.col_user')}</th>
              <th>{t('admin_payments.col_details')}</th>
              <th>{t('admin_payments.col_amount')}</th>
              <th>{t('admin_payments.col_status')}</th>
              <th>{t('admin_payments.col_reminders')}</th>
              <th>{t('admin_payments.col_date')}</th>
              <th>{t('admin_payments.col_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const overdue = Number(p.is_overdue) === 1
              const sent    = Number(p.reminders_sent || 0)
              return (
              <tr key={p.id}>
                <td>{p.user_name}<br /><span className="muted small">{p.user_email}</span></td>
                <td>{p.participant_names} <span className="muted small">· {p.course_names}</span></td>
                <td>{fmtPrice(p.amount)}</td>
                <td>
                  {t(`admin_payments.status_${p.status}`, { defaultValue: p.status })}
                  {overdue && (
                    <>
                      <br />
                      <span className="error small">
                        {t('admin_payments.overdue_badge', { count: Number(p.days_overdue) })}
                      </span>
                      {p.due_date && (
                        <>
                          <br />
                          <span className="muted small">
                            {t('admin_payments.due_on', { date: p.due_date })}
                          </span>
                        </>
                      )}
                    </>
                  )}
                </td>
                <td className="small">
                  {sent > 0
                    ? <>
                        {t('admin_payments.reminders_sent', { count: sent })}
                        {p.last_reminder_at && (
                          <><br /><span className="muted">{p.last_reminder_at}</span></>
                        )}
                      </>
                    : <span className="muted">&mdash;</span>}
                </td>
                <td className="small">{p.generated_at}</td>
                <td>
                  {(p.status === 'pending' || p.status === 'paid') && (
                    <button
                      type="button"
                      className="link-button danger"
                      disabled={busyId === p.id}
                      onClick={() => cancel(p)}
                    >
                      {p.status === 'paid' ? t('admin_payments.refund') : t('admin_payments.cancel')}
                    </button>
                  )}
                </td>
              </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </main>
  )
}
